import type { FC, MouseEvent, PropsWithChildren } from 'react';

import { useEffect, useState } from 'react';
import { styled, css } from 'styled-components';

import { breakpoints } from '../../../shared/core/styles';
import type { ProductVideo as ProductVideoSchema } from '~/amplienceTypes/schemas/imported/product-video-schema';
import { LargeDesktopLayout } from '~/components/pdp/productDetail/productImageGallery/LargeDesktopLayout';
import type { CarouselImageProperty } from '~/components/pdp/productDetail/productImageGallery/ProductImage';
import { SmallDesktopLayout } from '~/components/pdp/productDetail/productImageGallery/SmallDesktopLayout';
import { getRowPattern } from '~/components/pdp/productDetail/productImageGallery/utils/rowPattern';
import { CodeFeatureFlag } from '~/config/code-feature-flags';
import { useGalleryContext } from '~/utilities/context/dynamic/GalleryContext';
import { useNavActionContext } from '~/utilities/context/dynamic/NavActionContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { usePDPContext } from '~/utilities/context/static/PDPContext';
import { SiteType, VideoMode } from '~/utilities/graphql/codegen';

interface ProductScrolledViewProps {
  showAllImages?: boolean;
  productImageList: CarouselImageProperty[];
  setZoomPanelScrollXCenter: (e: MouseEvent) => void;
  setCurrentImageIndex: (idx: number) => void;
  videoIndex?: number;
  largeDesktopBreakpoint: number;
  productVideo: {
    content: ProductVideoSchema;
    [k: string]: unknown;
  };
}

const S = {
  SigningsWrapper: styled.div`
    ${({ theme }) => css`
      position: absolute;
      inset-inline-start: ${theme.spacing.x.xs};
      inset-block-end: ${theme.spacing.y.xs};
    `}
  `,
};

const ImageGap: Record<SiteType, string> = {
  [SiteType.Aaf]: '2px',
  [SiteType.Employeeshop]: '1px',
  [SiteType.Gstar]: '1px',
  [SiteType.Outlet]: '1px',
  [SiteType.Wahts]: '4px',
};

type Props = PropsWithChildren<ProductScrolledViewProps>;

/**
 * Scrolled View Gallery
 *
 * Logic
 * ----------------------------------------------------
 * Left nav closed screen < 1300px - Single image shown
 * Left nav closed screen >= 1300px - Image pair shown
 * Left nav open screen < 1401px - Single image shown
 * Left nav open screen >= 1401px -  Image pair shown
 *
 * @param productImageList
 * @param setCurrentImageIndex
 * @param setZoomPanelScrollXCenter
 */
export const ProductScrolledView: FC<Props> = ({
  showAllImages,
  videoIndex = 0,
  productImageList,
  productVideo,
  setCurrentImageIndex,
  setZoomPanelScrollXCenter,
  largeDesktopBreakpoint,
  children,
}) => {
  const {
    product: { videos },
  } = usePDPContext();
  const { sideNavOpened } = useNavActionContext();
  const { galleryZoomIn } = useGalleryContext();
  const { siteType } = useAppContext();
  const [isImagePair, setIsImagePair] = useState(false);
  const [isSmallDesktopLayout, setIsSmallDesktopLayout] = useState(false);

  const { unpackedGallery } = CodeFeatureFlag[siteType];

  useEffect(() => {
    if (unpackedGallery) {
      return undefined;
    }

    const updateImageDisplay = () => {
      const width = window.innerWidth;

      if (sideNavOpened) {
        setIsImagePair(width >= 1401);
      } else {
        setIsImagePair(width >= 1300);
      }
    };

    updateImageDisplay();

    window.addEventListener('resize', updateImageDisplay);

    return () => {
      window.removeEventListener('resize', updateImageDisplay);
    };
  }, [sideNavOpened, unpackedGallery]);

  useEffect(() => {
    if (!unpackedGallery) {
      setIsSmallDesktopLayout(false);

      return () => {};
    }

    const updateLayout = () => {
      const width = window.innerWidth;

      setIsSmallDesktopLayout(
        width >= Number.parseInt(breakpoints.lg, 10) && width < largeDesktopBreakpoint
      );
    };

    updateLayout();
    window.addEventListener('resize', updateLayout);

    return () => {
      window.removeEventListener('resize', updateLayout);
    };
  }, [largeDesktopBreakpoint, unpackedGallery]);

  const video = videos?.find(v => v?.mode === VideoMode.Landscape);
  const imageItems = productImageList.map((item, idx) => ({
    type: 'image' as const,
    key: item.original.url,
    index: idx,
    original: item.original,
    lazyLoad: item.lazyLoad,
  }));
  const galleryItems =
    unpackedGallery && video
      ? [
          ...imageItems.slice(0, videoIndex),
          {
            type: 'video' as const,
            key: 'video',
          },
          ...imageItems.slice(videoIndex),
        ]
      : imageItems;
  const totalItems = galleryItems.length;
  const rowPattern = getRowPattern(totalItems);

  const VISIBLE_ROWS = rowPattern[1] === 1 ? 4 : 3;
  const visibleCount = showAllImages
    ? totalItems
    : rowPattern.slice(0, VISIBLE_ROWS).reduce((sum, value) => sum + value, 0);

  return (
    <>
      {isSmallDesktopLayout ? (
        <SmallDesktopLayout
          unpackedGallery={unpackedGallery}
          galleryItems={galleryItems}
          rowPattern={rowPattern}
          visibleCount={visibleCount}
          showAllImages={showAllImages}
          productImageList={productImageList}
          productVideoContent={productVideo?.content}
          video={video}
          setCurrentImageIndex={setCurrentImageIndex}
          setZoomPanelScrollXCenter={setZoomPanelScrollXCenter}
          gap={ImageGap[siteType]}
          largeDesktopBreakpoint={largeDesktopBreakpoint}
        >
          {!galleryZoomIn && <S.SigningsWrapper>{children}</S.SigningsWrapper>}
        </SmallDesktopLayout>
      ) : (
        <LargeDesktopLayout
          unpackedGallery={unpackedGallery}
          productImageList={productImageList}
          showAllImages={showAllImages}
          video={video}
          productVideoContent={productVideo?.content}
          videoIndex={videoIndex}
          isImagePair={isImagePair}
          setCurrentImageIndex={setCurrentImageIndex}
          setZoomPanelScrollXCenter={setZoomPanelScrollXCenter}
          gap={ImageGap[siteType]}
          largeDesktopBreakpoint={largeDesktopBreakpoint}
        >
          {!galleryZoomIn && <S.SigningsWrapper>{children}</S.SigningsWrapper>}
        </LargeDesktopLayout>
      )}
    </>
  );
};
